/*
 * Shared behaviour for the dcs- home page sections.
 *
 * Every initialiser is idempotent and keyed off a data attribute, so it is safe
 * to run again when the theme editor re-renders a section. No libraries.
 *
 * Hover behaviour is always mirrored on focus, and on touch it becomes a tap
 * toggle — a first tap never navigates.
 */
(function () {
  'use strict';

  var INIT = 'dcsInit';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isTouch() {
    return window.matchMedia('(hover: none)').matches;
  }

  /* ------------------------------------------------------------------ *
   * Stripe stack — one stripe open at a time
   * ------------------------------------------------------------------ */
  function initStripeStack(root) {
    var stacks = root.querySelectorAll('[data-dcs-stripe-stack]');

    Array.prototype.forEach.call(stacks, function (stack) {
      if (stack.dataset[INIT]) return;
      stack.dataset[INIT] = '1';

      var stripes = Array.prototype.slice.call(stack.querySelectorAll('[data-dcs-stripe]'));
      if (!stripes.length) return;

      function closeAll() {
        stripes.forEach(function (s) {
          s.classList.remove('is-open');
          s.setAttribute('aria-expanded', 'false');
        });
        // Class fallback for browsers without :has()
        stack.classList.remove('has-open');
      }

      function open(stripe) {
        closeAll();
        stripe.classList.add('is-open');
        stripe.setAttribute('aria-expanded', 'true');
        stack.classList.add('has-open');
      }

      stripes.forEach(function (stripe) {
        // Pointer devices preview on hover; touch devices do not fire this.
        stripe.addEventListener('mouseenter', function () {
          if (isTouch()) return;
          open(stripe);
        });

        stripe.addEventListener('focus', function () {
          open(stripe);
        });

        // Tap/click toggles. The stripe is a <button>, so it never navigates —
        // the expanded panel carries an explicit link instead.
        stripe.addEventListener('click', function () {
          if (stripe.classList.contains('is-open')) {
            closeAll();
          } else {
            open(stripe);
          }
        });
      });

      stack.addEventListener('mouseleave', function () {
        if (isTouch()) return;
        closeAll();
      });

      // Closing on focus leaving the stack keeps keyboard and mouse in step.
      stack.addEventListener('focusout', function (event) {
        if (!stack.contains(event.relatedTarget)) closeAll();
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Word-by-word phrase — fires once when the phrase scrolls into view
   * ------------------------------------------------------------------ */
  function initAssemble(root) {
    var phrases = root.querySelectorAll('[data-dcs-assemble]');

    Array.prototype.forEach.call(phrases, function (phrase) {
      if (phrase.dataset[INIT]) return;
      phrase.dataset[INIT] = '1';

      if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
        phrase.classList.add('is-in');
        return;
      }

      /*
       * A threshold of 0.6 can never be met when the phrase is taller than the
       * viewport, which would leave the words stuck at opacity 0 — invisible
       * text, not just a missing animation. Hence the lower threshold and the
       * failsafe below.
       */
      var observer = new IntersectionObserver(
        function (entries, obs) {
          if (entries[0].isIntersecting) {
            phrase.classList.add('is-in');
            obs.disconnect();
            clearTimeout(failsafe);
          }
        },
        { threshold: 0.25 }
      );

      var failsafe = setTimeout(function () {
        phrase.classList.add('is-in');
        observer.disconnect();
      }, 2500);

      observer.observe(phrase);
    });
  }

  /* ------------------------------------------------------------------ *
   * Price decomposition bar — hover/focus opens, tap toggles
   * ------------------------------------------------------------------ */
  function initPriceBar(root) {
    var bars = root.querySelectorAll('[data-dcs-price-bar]');

    Array.prototype.forEach.call(bars, function (bar) {
      if (bar.dataset[INIT]) return;
      bar.dataset[INIT] = '1';

      var wrap = bar.closest('[data-dcs-price-wrap]');

      function set(open) {
        bar.classList.toggle('is-open', open);
        if (wrap) wrap.classList.toggle('is-open', open);
        bar.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      bar.addEventListener('mouseenter', function () {
        if (isTouch()) return;
        set(true);
      });
      bar.addEventListener('mouseleave', function () {
        if (isTouch()) return;
        set(false);
      });
      bar.addEventListener('focus', function () {
        set(true);
      });
      bar.addEventListener('blur', function () {
        set(false);
      });
      bar.addEventListener('click', function () {
        set(!bar.classList.contains('is-open'));
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Rejected fake discount — one deliberate "we never do this" moment
   * ------------------------------------------------------------------ */
  function initFakeDiscount(root) {
    var fakes = root.querySelectorAll('[data-dcs-fake-discount]');

    Array.prototype.forEach.call(fakes, function (fake) {
      if (fake.dataset[INIT]) return;
      fake.dataset[INIT] = '1';

      fake.addEventListener('click', function () {
        fake.classList.remove('is-rejected');
        // Force reflow so the animation can restart on repeat clicks.
        void fake.offsetWidth;
        fake.classList.add('is-rejected');
        fake.setAttribute('aria-pressed', 'true');
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Marquee — pause when the tab is hidden so it costs nothing offscreen
   * ------------------------------------------------------------------ */
  function initMarquee(root) {
    var marquees = root.querySelectorAll('[data-dcs-marquee]');

    Array.prototype.forEach.call(marquees, function (marquee) {
      if (marquee.dataset[INIT]) return;
      marquee.dataset[INIT] = '1';

      // Hover/focus pausing is CSS. This only handles tab visibility.
      document.addEventListener('visibilitychange', function () {
        marquee.classList.toggle('is-paused', document.hidden);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Hero loop — honour reduced motion, and cost nothing off-screen
   * ------------------------------------------------------------------ */
  function initHeroVideo(root) {
    var videos = root.querySelectorAll('.dcs-hero-drift__video');

    Array.prototype.forEach.call(videos, function (video) {
      if (video.dataset[INIT]) return;
      video.dataset[INIT] = '1';

      /*
       * An autoplaying loop is exactly what a reduced-motion preference is
       * about, so it is stopped on its poster frame rather than merely slowed.
       */
      // Hidden on phones by CSS, so streaming it there would be pure waste.
      if (window.matchMedia('(max-width: 989px)').matches) {
        video.removeAttribute('autoplay');
        video.setAttribute('preload', 'none');
        video.pause();
        return;
      }

      if (prefersReducedMotion()) {
        video.removeAttribute('autoplay');
        video.setAttribute('controls', 'controls');
        video.pause();
        return;
      }

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          video.pause();
        } else {
          var resumed = video.play();
          if (resumed && resumed.catch) resumed.catch(function () {});
        }
      });
    });
  }

  function initAll(root) {
    var scope = root || document;
    initStripeStack(scope);
    initAssemble(scope);
    initPriceBar(scope);
    initFakeDiscount(scope);
    initMarquee(scope);
    initHeroVideo(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor: re-run for a section that was just re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
